import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api"; 
import { toast } from "react-toastify";

const CreateVoucherPage = ({ projectId: propProjectId }) => {
    // --- AUTO-DETECT PROJECT LOGIC ---
    const getDefaultProjectDetails = () => {
        try {
            const storedUser = localStorage.getItem("currentUser") || localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                if (user.projects && user.projects.length > 0) {
                    return user.projects[0].projectId;
                }
            }
        } catch (err) {
            console.error("Error parsing user data", err);
        }
        return null;
    };

    // Determine Project ID: Prop -> LocalStorage -> Default from User Object
    const projectId = propProjectId || localStorage.getItem("selectedProjectId") || getDefaultProjectDetails();

    const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);
    const [allBeneficiaries, setAllBeneficiaries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(""); 
    const [showBeneficiaryPopup, setShowBeneficiaryPopup] = useState(false);
    const [selectedVendorIds, setSelectedVendorIds] = useState([]);
    const [allVendors, setAllVendors] = useState([]);
    
    const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const fetchAllVendors = useCallback(async (projId) => {
        try {
            const response = await api.get(`/vres/projects/${projId}/vendors`);
            setAllVendors(response.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to fetch vendors.");
        }
    }, []);

    const fetchBeneficiaries = useCallback(async (projId) => {
        try {
            const response = await api.get(`/vres/projects/${projId}/approved-beneficiaries`);
            setAllBeneficiaries(response.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to fetch beneficiaries.");
        }
    }, []);

    useEffect(() => {
        if (projectId) {
            fetchBeneficiaries(projectId);
            fetchAllVendors(projectId);
            setError("");
        } else {
            const noProjectMsg = "No project found. Please ensure you are assigned to a project.";
            // Only show error if we really can't find a project
            setError(noProjectMsg);
        }
    }, [projectId, fetchBeneficiaries, fetchAllVendors]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsVendorDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleVendor = (vendorId) => {
        setSelectedVendorIds((prev) => {
            if (prev.includes(vendorId)) {
                return prev.filter((id) => id !== vendorId);
            } else {
                return [...prev, vendorId];
            }
        });
    };

    const getSelectedVendorNames = () => {
        if (selectedVendorIds.length === 0) return "Select Vendors";
        const names = allVendors
            .filter(v => selectedVendorIds.includes(v.userId))
            .map(v => v.name);
        return names.join(", ");
    };

    // --- Helper to clear form selections ---
    const handleCancel = () => {
        setSelectedBeneficiaries([]);
        setSelectedVendorIds([]);
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!projectId) {
            toast.error("Project ID is missing.");
            return;
        }

        if (selectedBeneficiaries.length === 0) {
            toast.error("Please select beneficiaries.");
            return;
        }
        setIsLoading(true);

        const payload = {
            beneficiaryIds: selectedBeneficiaries.map((b) => b.beneficiaryId),
            vendors: selectedVendorIds,
        };

        try {
            const response = await api.post(`/vres/projects/${projectId}/vouchers`, payload);
            toast.success(response.data.message || "Vouchers issued successfully!");
            
            // ✅ AUTO-REFRESH LOGIC
            // 1. Clear selections
            handleCancel(); 
            // 2. Re-fetch beneficiaries from backend to update the list
            fetchBeneficiaries(projectId); 

        } catch (err) {
            console.error("Voucher creation error:", err);
            let displayMessage = "Failed to issue vouchers. Please try again.";

            if (err.response && err.response.data) {
                const backendData = err.response.data;
                let backendMessage = "";
                if (backendData && typeof backendData === 'object' && backendData.message) {
                    backendMessage = backendData.message;
                } else if (typeof backendData === 'string') {
                    backendMessage = backendData;
                }

                if (backendMessage) {
                    displayMessage = `Voucher creation failed: ${backendMessage}`;
                } else if (err.response.status === 403) {
                    displayMessage = "Voucher creation failed: You do not have permission.";
                } else {
                    displayMessage = `Voucher creation failed: Request failed with status code ${err.response.status}.`;
                }
            } else if (err.message) {
                displayMessage = `Voucher creation failed: ${err.message}`;
            }

            setError(displayMessage);
            toast.error(displayMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Select All Logic ---
    const handleSelectAllBeneficiaries = (e) => {
        if (e.target.checked) {
            setSelectedBeneficiaries(allBeneficiaries);
        } else {
            setSelectedBeneficiaries([]);
        }
    };

    const toggleBeneficiarySelection = (beneficiary) => {
        setSelectedBeneficiaries((prev) => {
            const exists = prev.some((b) => b.beneficiaryId === beneficiary.beneficiaryId);
            return exists
                ? prev.filter((b) => b.beneficiaryId !== beneficiary.beneficiaryId)
                : [...prev, beneficiary];
        });
    };

    const isAllBeneficiariesSelected = allBeneficiaries.length > 0 && selectedBeneficiaries.length === allBeneficiaries.length;

    // --- STYLES INJECTION ---
    const PageStyleBlock = () => (
        <style>{`
            .cv-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
            .cv-btn:active:not(:disabled) { transform: translateY(0); }
            .cv-btn:disabled { opacity: 0.6; cursor: not-allowed; }
            .cv-checkbox { accent-color: #20c997; width: 16px; height: 16px; cursor: pointer; }
            .cv-modal-table th { background-color: #f8f9fa; color: #636e72; font-weight: 600; padding: 12px; border-bottom: 2px solid #e1e4e8; text-align: left; }
            .cv-modal-table td { padding: 12px; border-bottom: 1px solid #e1e4e8; color: #2d3436; }
            .cv-modal-table tr:hover { background-color: #f0fdf9; }
        `}</style>
    );

    return (
        <div style={styles.formWrapper}>
            <PageStyleBlock />
            
            <div style={styles.formCard}>
                <h2 style={styles.formTitle}>Create Voucher</h2>
                
                {/* Optional: Show Project ID for confirmation */}
                {!error && projectId && (
                    <div style={{textAlign: 'center', marginBottom: '15px', fontSize: '0.85rem', color: '#636e72'}}>
                        Project ID: <strong>{projectId}</strong>
                    </div>
                )}

                {error && <div style={{ color: "#e74c3c", marginBottom: 15, textAlign: 'center', fontWeight: '500', backgroundColor: '#fdecea', padding: '10px', borderRadius: '4px' }}>{error}</div>}
                
                <form onSubmit={handleSubmit} style={styles.form}>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Beneficiaries</label>
                        <button 
                            type="button" 
                            onClick={() => setShowBeneficiaryPopup(true)} 
                            className="cv-btn"
                            style={styles.btnSelect}
                        >
                            Select Beneficiaries ({selectedBeneficiaries.length})
                        </button>
                    </div>

                    <div style={styles.formGroup} ref={dropdownRef}>
                        <label style={styles.formLabel}>Select Vendors</label>
                        <div style={{ position: 'relative' }}>
                            <div 
                                style={styles.dropdownHeader} 
                                onClick={() => setIsVendorDropdownOpen(!isVendorDropdownOpen)}
                            >
                                <span style={styles.selectedText}>
                                    {getSelectedVendorNames()}
                                </span>
                                <span style={styles.arrow}>{isVendorDropdownOpen ? '▲' : '▼'}</span>
                            </div>

                            {isVendorDropdownOpen && (
                                <div style={styles.dropdownList}>
                                    {allVendors.length > 0 ? (
                                        allVendors.map((vendor) => {
                                            const isSelected = selectedVendorIds.includes(vendor.userId);
                                            return (
                                                <div 
                                                    key={vendor.userId} 
                                                    onClick={() => toggleVendor(vendor.userId)}
                                                    style={{
                                                        ...styles.vendorOption,
                                                        backgroundColor: isSelected ? "#e6fffa" : "#fff", // Light Teal
                                                        color: isSelected ? "#20c997" : "#333", // Darker Teal
                                                        fontWeight: isSelected ? "600" : "400"
                                                    }}
                                                >
                                                    <input 
                                                        type="checkbox" 
                                                        className="cv-checkbox"
                                                        checked={isSelected} 
                                                        readOnly 
                                                        style={{ marginRight: "10px" }}
                                                    />
                                                    {vendor.name}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div style={{ padding: "10px", color: "#777" }}>No vendors available.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={styles.btnGroup}>
                        
                        <button 
                            type="button" 
                            onClick={handleCancel} 
                            className="cv-btn"
                            style={{ ...styles.btn, ...styles.btnSecondary }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={isLoading} 
                            className="cv-btn"
                            style={{ ...styles.btn, ...styles.btnPrimary }}
                        >
                            {isLoading ? "Submitting..." : "Issue Voucher"}
                        </button>
                    </div>
                </form>

                {showBeneficiaryPopup && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalContent}>
                            <h3 style={{ margin: '0 0 20px 0', color: '#2d3436' }}>Select Beneficiaries</h3>
                            
                            {allBeneficiaries.length > 0 ? (
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="cv-modal-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px', textAlign: 'center' }}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="cv-checkbox"
                                                        onChange={handleSelectAllBeneficiaries}
                                                        checked={isAllBeneficiariesSelected}
                                                    />
                                                </th>
                                                <th>Name</th>
                                                <th>Phone</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allBeneficiaries.map((b) => (
                                                <tr key={b.beneficiaryId}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="cv-checkbox"
                                                            checked={selectedBeneficiaries.some((sel) => sel.beneficiaryId === b.beneficiaryId)}
                                                            onChange={() => toggleBeneficiarySelection(b)}
                                                        />
                                                    </td>
                                                    <td>{b.name}</td>
                                                    <td>{b.phone}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p style={{ textAlign: 'center', color: '#636e72' }}>No approved beneficiaries found for this project.</p>
                            )}
                            
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                                <button onClick={() => setShowBeneficiaryPopup(false)} className="cv-btn" style={{ ...styles.btn, ...styles.btnPrimary }}>Done</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- STYLES (Updated to Teal Theme) ---
const styles = {
    formWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center', 
        width: '100%',
        minHeight: 'calc(100vh - 80px)', 
        padding: '30px',
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        backgroundColor: '#f0fdf9', // Teal Background
    },
    formCard: {
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '600px', 
        padding: '30px 40px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        borderTop: '5px solid #20c997', // Teal Border
        margin: '20px auto',
    },
    formTitle: {
        textAlign: 'center',
        marginBottom: '25px',
        fontSize: '1.5rem',
        color: '#2d3436', // Dark Grey
        fontWeight: '700',
        marginTop: 0,
    },
    form: { display: "grid", gridTemplateColumns: "1fr", gap: "20px" },
    formGroup: { width: "100%" },
    formLabel: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#2d3436', 
        fontSize: '0.9rem',
    },
    btnSelect: {
        width: "100%",
        padding: "12px",
        backgroundColor: "#e6fffa", // Light Teal
        color: "#00b894", // Darker Teal Text
        border: "1px solid #b2f2bb",
        borderRadius: "6px",
        cursor: "pointer",
        textAlign: "left",
        fontSize: "0.9rem",
        fontWeight: "600",
        transition: 'all 0.2s',
    },
    dropdownHeader: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "6px",
        border: "1px solid #dcdcdc",
        backgroundColor: "white",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxSizing: "border-box",
        minHeight: "45px"
    },
    selectedText: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginRight: "10px",
        flex: 1,
        fontSize: "0.9rem",
        color: "#2d3436",
    },
    dropdownList: {
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        backgroundColor: "#fff",
        border: "1px solid #ced4da",
        borderRadius: "0 0 6px 6px",
        maxHeight: "200px",
        overflowY: "auto",
        zIndex: 1000,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
    },
    vendorOption: {
        padding: "10px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #f1f1f1",
        transition: "background-color 0.2s",
        fontSize: "0.9rem",
    },
    btnGroup: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '15px',
        marginTop: '20px',
    },
    btn: {
        padding: '10px 24px',
        borderRadius: '6px',
        fontSize: '0.9rem',
        fontWeight: '600',
        cursor: 'pointer',
        border: 'none',
        minWidth: '120px',
        transition: 'all 0.2s',
    },
    btnPrimary: {
        backgroundColor: '#20c997', // Teal Primary
        color: 'white',
        boxShadow: '0 4px 6px rgba(32, 201, 151, 0.2)',
    },
    btnSecondary: {
        backgroundColor: '#636e72', // Grey
        color: 'white',
    },
    arrow: {
        fontSize: "12px",
        color: "#666"
    },
    modalOverlay: {
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 
    },
    modalContent: { 
        background: "white", padding: 30, borderRadius: 12, width: "90%", maxWidth: "600px", 
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
    },
};

export default CreateVoucherPage;